// Credits Modal Component
// Handles the credit balance display and Google Play purchase flow via Android WebView bridge.
import { addCredits, CREDIT_PACKAGES, getUserCredits } from '../firebase/credits.js';
import { getCurrentUser } from '../firebase/auth.js';
import { toast } from './Toast.js';
import { t } from '../i18n.js';

/**
 * Detect whether the web app is running inside the ArchaeoHub Android WebView.
 * The Android app exposes `window.ArchaeoHubBridge` via addJavascriptInterface().
 */
export function isAndroidApp() {
    return typeof window.ArchaeoHubBridge !== 'undefined';
}

/**
 * Create the Credits modal HTML and append it to body (once).
 */
export function initCreditsModal() {
    if (document.getElementById('credits-modal-backdrop')) return; // already mounted

    const el = document.createElement('div');
    el.innerHTML = createCreditsModalHTML();
    document.body.appendChild(el.firstElementChild);

    // Set up purchase button listeners
    document.querySelectorAll('[data-sku]').forEach(btn => {
        btn.addEventListener('click', () => {
            const sku = btn.dataset.sku;
            triggerPurchase(sku);
        });
    });

    // Close button
    document.getElementById('credits-modal-close')?.addEventListener('click', hideCreditsModal);
    document.getElementById('credits-modal-backdrop')?.addEventListener('click', (e) => {
        if (e.target.id === 'credits-modal-backdrop') hideCreditsModal();
    });

    // Register global callbacks that the Android app will call
    registerAndroidCallbacks();
}

function createCreditsModalHTML() {
    const packages = CREDIT_PACKAGES.map(pkg => {
        const isBestValue = pkg.credits === 50;
        return `
            <div class="credit-pkg-card ${isBestValue ? 'best-value' : ''}" data-sku="${pkg.id}">
                ${isBestValue ? `<div class="best-value-badge">BEST VALUE</div>` : ''}
                <div class="credit-pkg-icon">${pkg.credits >= 100 ? '💎' : pkg.credits >= 50 ? '🏛️' : '🏺'}</div>
                <div class="credit-pkg-amount">$${pkg.price}</div>
                <div class="credit-pkg-credits">${pkg.credits} ${t('credits.creditLabel')}</div>
                <button class="btn ${isBestValue ? 'btn-primary' : 'btn-secondary'} btn-sm credit-pkg-btn" data-sku="${pkg.id}">${t('credits.buy')}</button>
            </div>
        `;
    }).join('');

    const androidNotice = isAndroidApp() ? '' : `
        <div class="credits-app-notice glass-effect">
            <span>📱</span>
            <p>${t('credits.webNotice')}</p>
        </div>
    `;

    return `
    <div class="modal-backdrop" id="credits-modal-backdrop">
        <div class="modal credits-modal glass-effect">
            <div class="modal-header">
                <h3 class="modal-title text-gradient">💳 ${t('credits.buy')}</h3>
                <button class="modal-close" id="credits-modal-close">✕</button>
            </div>
            <div class="modal-body">
                <div class="credits-balance-display-modern">
                    <div class="balance-info">
                        <div class="credits-balance-label">${t('credits.currentBalance')}</div>
                        <div class="credits-balance-value" id="credits-modal-balance">—</div>
                    </div>
                    <div class="balance-visual">
                        <div class="coin-stack">💰</div>
                    </div>
                </div>

                <div class="credits-assembly-notice-modern">
                    <span class="pulse-icon">🧩</span>
                    <span>${t('credits.assemblyPrice')}</span>
                </div>

                ${androidNotice}

                <div class="credit-packages ${!isAndroidApp() ? 'credit-packages-disabled' : ''}">
                    ${packages}
                </div>

                <div class="credits-history-link">
                    <a href="#" id="credits-history-toggle" class="btn btn-ghost btn-sm">${t('credits.history')}</a>
                </div>
                <div class="credits-history hidden" id="credits-history">
                    <p class="text-muted text-sm text-center">${t('app.loading')}</p>
                </div>
            </div>
        </div>
    </div>

    <style>
        .credits-modal { 
            max-width: 580px; 
            border: 1px solid rgba(255, 255, 255, 0.1);
            overflow: visible;
        }

        .credits-balance-display-modern {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: linear-gradient(135deg, rgba(212, 168, 83, 0.15), rgba(0, 0, 0, 0.2));
            border: 1px solid rgba(212, 168, 83, 0.3);
            border-radius: var(--radius-2xl);
            padding: var(--space-6) var(--space-8);
            margin-bottom: var(--space-6);
            position: relative;
            overflow: hidden;
        }

        .credits-balance-display-modern::after {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(212, 168, 83, 0.1) 0%, transparent 70%);
            pointer-events: none;
        }
        
        .credits-balance-label { 
            font-size: var(--text-xs); 
            color: var(--color-text-tertiary); 
            text-transform: uppercase; 
            letter-spacing: 0.1em;
            margin-bottom: 4px;
        }

        .credits-balance-value { 
            font-size: var(--text-4xl); 
            font-weight: 800; 
            color: var(--color-accent-primary);
            text-shadow: 0 0 20px rgba(212, 168, 83, 0.3);
        }

        .balance-visual { font-size: 3rem; filter: drop-shadow(0 0 10px rgba(212, 168, 18, 0.3)); }

        .credits-assembly-notice-modern {
            display: flex;
            align-items: center;
            gap: var(--space-3);
            font-size: var(--text-sm);
            color: var(--color-text-secondary);
            margin-bottom: var(--space-6);
            padding: var(--space-3) var(--space-4);
            background: rgba(255, 255, 255, 0.03);
            border-radius: var(--radius-lg);
            border-left: 3px solid var(--color-accent-primary);
        }

        .pulse-icon {
            animation: pulse-soft 2s infinite;
        }

        .credit-packages {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: var(--space-4);
            margin-bottom: var(--space-6);
        }

        @media (min-width: 600px) {
            .credit-packages { grid-template-columns: repeat(4, 1fr); }
        }

        .credit-pkg-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.08);
            border-radius: var(--radius-xl);
            padding: var(--space-5) var(--space-3);
            text-align: center;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .credit-pkg-card:hover {
            transform: translateY(-5px);
            background: rgba(255, 255, 255, 0.06);
            border-color: var(--color-accent-primary);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .credit-pkg-card.best-value {
            border: 1px solid var(--color-accent-primary);
            background: linear-gradient(180deg, rgba(212, 168, 83, 0.1), transparent);
        }

        .best-value-badge {
            position: absolute;
            top: -12px;
            left: 50%;
            transform: translateX(-50%);
            background: var(--color-accent-primary);
            color: var(--color-bg-primary);
            font-size: 10px;
            font-weight: 800;
            padding: 2px 10px;
            border-radius: 20px;
            white-space: nowrap;
            letter-spacing: 0.05em;
            box-shadow: 0 4px 10px rgba(212, 168, 83, 0.4);
        }

        .credit-pkg-icon { font-size: 1.8rem; margin-bottom: var(--space-2); }
        
        .credit-pkg-amount {
            font-size: var(--text-2xl);
            font-weight: 800;
            color: var(--color-text-primary);
            margin-bottom: 2px;
        }

        .credit-pkg-credits {
            font-size: var(--text-xs);
            color: var(--color-text-tertiary);
            margin-bottom: var(--space-4);
            font-weight: 500;
        }

        .credit-pkg-btn { width: 100%; font-weight: 600; }

        .credits-history-link { margin-top: var(--space-2); }
        
        @keyframes pulse-soft {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
        }

        .credits-app-notice {
            margin-bottom: var(--space-6);
        }
    </style>`;
}

/**
 * Show the credits modal and refresh balance
 */
export async function showCreditsModal() {
    let backdrop = document.getElementById('credits-modal-backdrop');
    if (!backdrop) {
        initCreditsModal();
        backdrop = document.getElementById('credits-modal-backdrop');
    }

    backdrop.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Refresh balance
    await refreshModalBalance();

    // History toggle
    const historyToggle = document.getElementById('credits-history-toggle');
    const historyContainer = document.getElementById('credits-history');
    historyToggle?.addEventListener('click', async (e) => {
        e.preventDefault();
        if (historyContainer.classList.toggle('hidden')) return;

        const user = getCurrentUser();
        if (!user) return;

        historyContainer.innerHTML = `<p class="text-muted text-sm text-center">${t('app.loading')}</p>`;

        try {
            const { getTransactionHistory } = await import('../firebase/credits.js');
            const txs = await getTransactionHistory(user.uid, 15);
            if (!txs.length) {
                historyContainer.innerHTML = `<p class="text-muted text-sm text-center">${t('credits.noHistory')}</p>`;
                return;
            }
            historyContainer.innerHTML = txs.map(tx => {
                const date = tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString() : '—';
                const cls = tx.amount > 0 ? 'tx-amount-plus' : 'tx-amount-minus';
                const sign = tx.amount > 0 ? '+' : '';
                return `
                    <div class="tx-item">
                        <span>${tx.description || tx.type}</span>
                        <span>${date}</span>
                        <span class="${cls}">${sign}${tx.amount}</span>
                    </div>`;
            }).join('');
        } catch (e) {
            historyContainer.innerHTML = `<p class="text-muted text-sm text-center">${t('app.error')}</p>`;
        }
    }, { once: true });
}

/**
 * Hide the modal
 */
export function hideCreditsModal() {
    document.getElementById('credits-modal-backdrop')?.classList.remove('active');
    document.body.style.overflow = '';
}

/**
 * Refresh just the balance number inside the modal
 */
export async function refreshModalBalance() {
    const user = getCurrentUser();
    if (!user) return;

    const el = document.getElementById('credits-modal-balance');
    if (el) {
        const credits = await getUserCredits(user.uid);
        el.textContent = `${credits} ${t('credits.creditLabel')}`;
    }
}

/**
 * Trigger a purchase via the Android bridge
 */
function triggerPurchase(skuId) {
    if (!isAndroidApp()) {
        toast.info(t('credits.webNotice'));
        return;
    }
    window.ArchaeoHubBridge.purchaseCredits(skuId);
}

/**
 * Register global callbacks for the Android app to call after a purchase attempt
 */
function registerAndroidCallbacks() {
    /**
     * Called by Android after a SUCCESSFUL Google Play purchase
     * @param {string} skuId
     * @param {string} orderId
     */
    window.archaeoHubOnPurchaseSuccess = async (skuId, orderId) => {
        const pkg = CREDIT_PACKAGES.find(p => p.id === skuId);
        if (!pkg) {
            toast.error('Unknown package: ' + skuId);
            return;
        }

        const user = getCurrentUser();
        if (!user) return;

        try {
            const newBalance = await addCredits(user.uid, pkg.credits, skuId, orderId);
            toast.success(`${t('credits.purchaseSuccess')}: +${pkg.credits} ${t('credits.creditLabel')}`);

            // Update nav badge
            updateNavCreditBadge(newBalance);

            // Update modal if open
            const el = document.getElementById('credits-modal-balance');
            if (el) el.textContent = `${newBalance} ${t('credits.creditLabel')}`;
        } catch (e) {
            console.error('Credit write error:', e);
            toast.error(t('app.error'));
        }
    };

    window.archaeoHubOnPurchaseFailed = (reason) => {
        console.warn('Purchase failed:', reason);
        toast.error(`${t('credits.purchaseFailed')}: ${reason}`);
    };

    window.archaeoHubOnPurchaseCancelled = () => {
        toast.info(t('credits.purchaseCancelled'));
    };
}

/**
 * Update the credit badge shown in the navigation bar
 */
export function updateNavCreditBadge(credits) {
    const badge = document.getElementById('nav-credits-badge');
    if (badge) {
        badge.textContent = `💳 ${credits}`;
    }
}
